/**
 * Servers Service
 * Handles business logic for platform servers and groups management
 */

import serversRepository from '../repositories/servers.repository'
import { Server, ServerGroup, ServerGroupsResponse } from '../types'

// Extended server type with loaded groups
export interface ServerWithGroups extends Server {
  groups: ServerGroup[]
  groupsLoaded: boolean
  groupsLoading: boolean
  expanded: boolean
}

class ServersService {
  /**
   * Get all servers for the current partner
   * @returns Promise with array of servers
   */
  async getServers(): Promise<ServerWithGroups[]> {
    try {
      const servers = await serversRepository.getServers()
      // Transform to include groups state
      return servers.map(server => ({
        ...server,
        groups: [],
        groupsLoaded: false,
        groupsLoading: false,
        expanded: false
      }))
    } catch (error) {
      console.error('Failed to fetch servers:', error)
      throw error
    }
  }

  /**
   * Get groups for a specific server
   * @param serverId - The server ID
   * @returns Promise with server groups
   */
  async getServerGroups(serverId: number): Promise<ServerGroupsResponse> {
    try {
      return await serversRepository.getServerGroups(serverId)
    } catch (error) {
      console.error('Failed to fetch server groups:', error)
      throw error
    }
  }

  /**
   * Update group nickname
   * @param groupId - The group ID
   * @param nickname - The new nickname
   * @returns Promise with updated group
   */
  async updateGroupNickname(groupId: number, nickname: string): Promise<ServerGroup> {
    try {
      return await serversRepository.updateGroupNickname(groupId, nickname)
    } catch (error) {
      console.error('Failed to update group nickname:', error)
      throw error
    }
  }

  /**
   * Get platform logo URL based on version
   * @param version - MT4 or MT5
   * @returns Logo URL or default
   */
  getPlatformLogo(version: string): string {
    // Use platform-specific logos
    if (version === 'MT5') {
      return 'https://images.squarespace-cdn.com/content/v1/5d849fb37a733a2e699b3774/1588861945863-2ID74IHT0Y1VGKLYE497/Metatrader4'
    }
    return 'https://images.squarespace-cdn.com/content/v1/5d849fb37a733a2e699b3774/1588861945863-2ID74IHT0Y1VGKLYE497/Metatrader4?format=100'
  }

  /**
   * Format server type for display
   * @param type - LIVE or DEMO
   * @returns Formatted type string
   */
  formatServerType(type: string): string {
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
  }

  /**
   * Get server type color
   * @param type - LIVE or DEMO
   * @returns Color for the type badge
   */
  getServerTypeColor(type: string): string {
    return type === 'LIVE' ? '#28EA96' : '#ABABAB'
  }
}

export default new ServersService()
